import { useEffect, useRef, useState } from 'react'
import { Monitor, Smartphone, ZoomIn } from 'lucide-react'
import type { StudioDocument } from '../core/model'
import { SandboxPreview, type PreviewMetric } from './SandboxPreview'

export type PreviewViewport = 'desktop' | 'mobile'

interface CanvasSurfaceProps {
  document: StudioDocument
  selectedNodeId: string
  viewport: PreviewViewport
  onViewportChange: (viewport: PreviewViewport) => void
  onSelect: (nodeId: string) => void
  onMetric: (metric: PreviewMetric) => void
  onRuntimeError: (message: string | null) => void
  runtimeError: string | null
}

const viewportSize: Record<PreviewViewport, { width: number; height: number }> = {
  desktop: { width: 820, height: 660 },
  mobile: { width: 390, height: 720 },
}

export function CanvasSurface({
  document,
  selectedNodeId,
  viewport,
  onViewportChange,
  onSelect,
  onMetric,
  onRuntimeError,
  runtimeError,
}: CanvasSurfaceProps) {
  const stageRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0.8)
  const dimensions = viewportSize[viewport]

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const updateScale = () => {
      const availableWidth = Math.max(stage.clientWidth - 44, 180)
      const availableHeight = Math.max(stage.clientHeight - 44, 180)
      setScale(
        Math.min(
          availableWidth / dimensions.width,
          availableHeight / dimensions.height,
          1,
        ),
      )
    }
    updateScale()
    const observer = new ResizeObserver(updateScale)
    observer.observe(stage)
    return () => observer.disconnect()
  }, [dimensions.height, dimensions.width])

  return (
    <section className="canvas-surface" aria-label="Design canvas">
      <div className="surface-toolbar">
        <div className="surface-toolbar__group" aria-label="Preview viewport">
          <button
            className={viewport === 'desktop' ? 'is-active' : ''}
            type="button"
            aria-label="Desktop preview"
            aria-pressed={viewport === 'desktop'}
            onClick={() => onViewportChange('desktop')}
          >
            <Monitor size={13} />
          </button>
          <button
            className={viewport === 'mobile' ? 'is-active' : ''}
            type="button"
            aria-label="Mobile preview"
            aria-pressed={viewport === 'mobile'}
            onClick={() => onViewportChange('mobile')}
          >
            <Smartphone size={13} />
          </button>
        </div>
        <span className="viewport-label">
          {dimensions.width} × {dimensions.height}
        </span>
        <span className="zoom-label">
          <ZoomIn size={12} /> {Math.round(scale * 100)}%
        </span>
      </div>
      <div ref={stageRef} className="artboard-stage">
        <div
          className={`artboard artboard--${viewport}`}
          style={{
            width: dimensions.width,
            height: dimensions.height,
            transform: `translate(-50%, -50%) scale(${scale})`,
          }}
        >
          <SandboxPreview
            document={document}
            selectedNodeId={selectedNodeId}
            onSelect={onSelect}
            onMetric={onMetric}
            onRuntimeError={onRuntimeError}
          />
          {runtimeError ? (
            <div className="runtime-error" role="alert">
              <strong>Preview isolated</strong>
              <span>{runtimeError}</span>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}
